import React, { useEffect, useState } from 'react';
import { client, formatAddress, consoleLogInDev } from 'shared';
import { Colors } from 'library';
import { Card, EmptyText, Row, Stack, Text } from 'library/ui';
import { formatShortWeekdayMonthDay } from 'shared/Dates';
import { Address } from 'models';
import { WeeklyJobFeedback } from '../BusinessJob/models';
import { Assignment, DeleteForever } from '@mui/icons-material';
import { Options } from '../BusinessCalendar/Options';
import CancelAllModal from '../BusinessJob/CancelAllModal';

export interface IBusinessOngoing {
    id: number;
    headline: string;
    family_comments: string;
    status: string;
    start_date: string;
    end_date: string;
    business_job_type: string;
    address: Address;
    filled_count: number;
    shift_count: number;
    weekly_job_feedback: WeeklyJobFeedback[];
    expired?: boolean;
}

interface IShiftListProps {
    addressId?: number;
}

export default function ShiftList({ addressId }: IShiftListProps) {
    const [loading, setLoading] = useState(true);
    const [ongoingRequests, setOngoingRequests] = useState<IBusinessOngoing[]>([]);
    const [selectedId, setSelectedId] = useState<number>();

    function getUpcomingShifts() {
        if (addressId) {
            client(`api/business-ongoing/?address_id=${addressId}&show_active=true`)
                .then((response: IBusinessOngoing[]) => {
                    setOngoingRequests(response);
                })
                .catch(consoleLogInDev)
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    useEffect(() => {
        getUpcomingShifts();
    }, [addressId]);

    function getStatus(ongoingRequest: IBusinessOngoing) {
        if (!ongoingRequest.filled_count) {
            return 'Awaiting Placement';
        }

        if (ongoingRequest.shift_count && ongoingRequest.shift_count === ongoingRequest.filled_count) {
            return 'Filled';
        } else if (ongoingRequest.shift_count !== ongoingRequest.filled_count) {
            return 'Partially Filled';
        }

        return 'Awaiting Placement';
    }

    if (loading) {
        return <EmptyText>Loading...</EmptyText>;
    }

    return (
        <Stack gap="sm">
            {!ongoingRequests.length ? (
                <Text>
                    You currently have no confirmed shifts, post a job or reach out to us at{' '}
                    <a
                        style={{ color: Colors.primary, textDecorationLine: 'underline' }}
                        href="mailto:support@join-tandem.com"
                    >
                        support@join-tandem.com
                    </a>{' '}
                    to get started!
                </Text>
            ) : (
                ongoingRequests.map((ongoingRequest) => (
                    <Card key={ongoingRequest.id} padding="sm">
                        <Row justify="between" align="start" gap="sm">
                            <Stack gap="sm" grow>
                                <Row gap="sm" align="center" wrap>
                                    <Text size="md" weight="bold">
                                        {ongoingRequest.headline}
                                    </Text>
                                </Row>
                                <Text size="sm">{formatAddress(ongoingRequest.address)}</Text>
                            </Stack>
                            <Stack gap="sm" align="start">
                                <Text size="sm">
                                    <b>Start Date</b>:{' '}
                                    {formatShortWeekdayMonthDay(ongoingRequest.start_date.replaceAll('-', '/'))}
                                </Text>
                                <Text size="sm">
                                    <b>Status</b>: {getStatus(ongoingRequest)}
                                </Text>
                            </Stack>
                            <Options
                                options={[
                                    {
                                        text: 'Manage Job',
                                        icon: <Assignment />,
                                        to: `/jobs/${ongoingRequest.id}`,
                                    },
                                    {
                                        text: 'Cancel Entire Job',
                                        icon: <DeleteForever />,
                                        onClick: () => setSelectedId(ongoingRequest.id),
                                    },
                                ]}
                            />
                        </Row>
                    </Card>
                ))
            )}
            <CancelAllModal
                open={Boolean(selectedId)}
                onClose={() => setSelectedId(undefined)}
                ongoingRequestId={selectedId}
                refresh={getUpcomingShifts}
            />
        </Stack>
    );
}
